import { ChangeDetectionStrategy, Component, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { I18NextPipe } from 'angular-i18next';
import {
  AlertComponent,
  ButtonComponent,
  InputComponent,
  MbsPopupType,
  ModalComponent,
  ModalFooterDirective,
  ToastService
} from 'mbs-ui-kit';
import { PermissionsTreeComponent } from 'mbs-ui-shared@components/permissions-tree/permissions-tree.component';
import { PRESET_NAME_MAX_LENGTH } from 'mbs-ui-shared@constants/admin-permission-presets.constants';
import { AdminPermissionPresetsFacade } from 'mbs-ui-shared@facades/admin-permission-presets.facade';
import { AccountTypeEnum, Permissions } from 'mbs-ui-shared@models';
import { permissionsListToObject, permissionsObjectToList } from 'mbs-ui-shared@models/AdministratorInCamelCase';
import { AdminPermissionPreset } from 'mbs-ui-shared@models/AdminPermissionPreset';
import { McpSettingsService } from 'mbs-ui-shared@services/mcp-settings.service';
import { finalize, take } from 'rxjs';

@Component({
  selector: 'mbs-edit-preset-modal',
  templateUrl: './edit-preset-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AlertComponent,
    ButtonComponent,
    I18NextPipe,
    InputComponent,
    ModalComponent,
    ModalFooterDirective,
    PermissionsTreeComponent,
    ReactiveFormsModule
  ]
})
export class EditPresetModalComponent implements OnInit {
  public readonly MbsPopupType = MbsPopupType;

  private presetsFacade = inject(AdminPermissionPresetsFacade);
  private toastService = inject(ToastService);
  private i18N = inject(I18NextPipe);
  protected readonly mcpSettings = inject(McpSettingsService);

  // Presets are not bound to an administrator, so the tree shows everything the provider is allowed to grant
  public readonly accountType = AccountTypeEnum.Internal;

  public loading = false;
  public preset: AdminPermissionPreset;
  public permissions: Permissions;
  public nameControl = new FormControl<string>('', [Validators.required, Validators.maxLength(PRESET_NAME_MAX_LENGTH)]);

  @ViewChild(ModalComponent, { static: true }) baseModal: ModalComponent;

  ngOnInit(): void {
    this.mcpSettings.load();
    this.preset = this.baseModal.data.preset as AdminPermissionPreset;
    this.permissions = permissionsListToObject(this.preset.permissions);
    this.nameControl.setValue(this.preset.name);
  }

  public save(): void {
    this.loading = true;

    this.presetsFacade
      .update(this.preset.id, { name: this.nameControl.value.trim(), permissions: permissionsObjectToList(this.permissions) })
      .pipe(
        take(1),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: () => {
          this.toastService.success(this.i18N.transform('shared:administrators.presets.presetListSaved'));
          this.baseModal.save();
        },
        error: (err) => this.nameControl.setErrors({ custom: err?.error?.detail })
      });
  }
}
